'use client'

import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import * as z from 'zod'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { useLogger } from '@/lib/hooks/useLogger'
import { sendContactSalesEmail } from '@/app/actions/emails'

const formSchema = z.object({
  companyName: z.string().min(2, 'Company name is required'),
  teamSize: z.string({
    required_error: 'Please select a team size',
  }),
  name: z.string().min(2, 'Full name is required'),
  email: z.string().email('Invalid email address'),
  message: z.string().optional(),
})

interface Props {
  planType: 'team' | 'enterprise'
  onSuccess?: () => void
}

export function ContactSalesForm({ planType, onSuccess }: Props) {
  const logger = useLogger()
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [error, setError] = useState('')
  
  const {
    register,
    handleSubmit,
    formState: { errors },
    reset
  } = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema)
  })

  async function onSubmit(values: z.infer<typeof formSchema>) {
    setIsSubmitting(true)
    setError('')
    
    try {
      await sendContactSalesEmail({
        ...values,
        planType,
      })
      reset()
      onSuccess?.()
    } catch (error) {
      logger.error('Error sending contact form:', error)
      setError('Failed to send request. Please try again.')
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
      <div>
        <Input
          {...register('companyName')}
          placeholder="Company Name"
          className={errors.companyName ? 'border-red-500' : ''}
        />
        {errors.companyName && (
          <p className="text-red-500 text-sm mt-1">{errors.companyName.message}</p>
        )}
      </div>

      <div>
        <select
          {...register('teamSize')}
          className={`w-full p-2 border rounded ${errors.teamSize ? 'border-red-500' : 'border-gray-200'} dark:bg-gray-800`}
        >
          <option value="">Select team size</option>
          <option value="5-10">5-10 employees</option>
          <option value="11-50">11-50 employees</option>
          <option value="51-200">51-200 employees</option>
          <option value="201-1000">201-1000 employees</option>
          <option value="1000+">1000+ employees</option>
        </select>
        {errors.teamSize && (
          <p className="text-red-500 text-sm mt-1">{errors.teamSize.message}</p>
        )}
      </div>

      <div>
        <Input
          {...register('name')}
          placeholder="Full Name"
          className={errors.name ? 'border-red-500' : ''}
        />
        {errors.name && (
          <p className="text-red-500 text-sm mt-1">{errors.name.message}</p>
        )}
      </div>

      <div>
        <Input
          {...register('email')}
          placeholder="Work Email"
          className={errors.email ? 'border-red-500' : ''}
        />
        {errors.email && (
          <p className="text-red-500 text-sm mt-1">{errors.email.message}</p>
        )}
      </div>

      <div>
        <Textarea
          {...register('message')}
          placeholder="What got you interested in mixus?"
          className="resize-none min-h-[100px] border border-input"
        />
      </div>

      {error && (
        <p className="text-red-500 text-sm">{error}</p>
      )}

      <Button 
        type="submit" 
        className="w-full"
        disabled={isSubmitting}
      >
        {isSubmitting ? 'Sending...' : 'Submit Request'}
      </Button>
    </form>
  )
} 